// API service for communicating with the backend
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '')

/** Resolve an image path (e.g. /uploads/...) to a full URL usable in <img src>. */
export function resolveImageUrl(src: string | undefined | null): string {
  if (!src) return ''
  if (src.startsWith('data:') || src.startsWith('http://') || src.startsWith('https://') || src.startsWith('blob:')) return src
  return `${API_ORIGIN}${src.startsWith('/') ? '' : '/'}${src}`
}

/** Format order item display name with code prefix: "B7 - Schezuan Beef" */
export function formatItemName(item: { item_code?: string | null; item_name?: string; itemName?: string; name?: string } | null | undefined): string {
  if (!item) return 'Unknown Item'
  const name = item.item_name || item.itemName || item.name || 'Unknown Item'
  const code = item.item_code
  return code ? `${code} - ${name}` : name
}

/** Order item interface for consolidation */
export interface OrderItem {
  id: string
  item_name: string
  item_description?: string
  item_code?: string
  base_price: number
  variant_name?: string
  variant_price_modifier: number
  addons: any[]
  quantity: number
  unit_price: number
  total_price: number
  notes?: string
  status?: string
  kds_station_id?: string
  kds_stations?: {
    id: string
    name: string
    color: string
  } | null
}

/**
 * Consolidates duplicate order items that were created for multiple KDS stations.
 * Items are considered duplicates if they have the same:
 * - item_name
 * - item_code
 * - variant_name
 * - addons (compared by name and price)
 * - notes
 * - unit_price
 *
 * This prevents showing the same item multiple times in customer-facing views
 * (order details, receipts, invoices) when it's assigned to multiple KDS stations.
 */
/** When false, deduplicate by item but keep first occurrence's quantity (for bill request display when same item is sent to multiple KDS stations). Default true = sum quantities. */
export interface ConsolidateOrderItemsOptions {
  sumQuantities?: boolean
}

export function consolidateOrderItems(
  items: OrderItem[] | null | undefined,
  options?: ConsolidateOrderItemsOptions
): OrderItem[] {
  if (!items || items.length === 0) return []
  const sumQuantities = options?.sumQuantities !== false

  const consolidated = new Map<string, OrderItem>()

  for (const item of items) {
    // Create a unique key based on item properties that define "sameness"
    // Exclude id and kds_station_id since those differ for duplicates
    const addonsKey = item.addons && item.addons.length > 0
      ? item.addons.map((a: any) => `${a.name}:${a.price}`).sort().join('|')
      : ''

    const uniqueKey = [
      item.item_name || '',
      item.item_code || '',
      item.variant_name || '',
      addonsKey,
      item.notes || '',
      item.unit_price || 0
    ].join('::')

    if (consolidated.has(uniqueKey)) {
      if (sumQuantities) {
        const existing = consolidated.get(uniqueKey)!
        existing.quantity += item.quantity
        existing.total_price = (Number(existing.total_price) || 0) + (Number(item.total_price) || 0)
      }
      // else: same item from another KDS station — keep first occurrence only
    } else {
      consolidated.set(uniqueKey, { ...item })
    }
  }

  return Array.from(consolidated.values())
}

/**
 * Matches backend `deduplicateOrderItems` (server.js). Use for receipts/invoices so
 * additional-item lines (same menu row added later) stay separate, while KDS fan-out
 * in the same created_at bucket still collapses. `consolidateOrderItems` with sumQuantities:false
 * drops duplicate keys and wrongly omits additional items.
 */
export function deduplicateOrderItems(
  items: OrderItem[] | null | undefined,
  includeKdsInfo = false,
  sumQuantities = true
): OrderItem[] {
  if (!Array.isArray(items) || items.length === 0) return items || []

  const normalizeStatus = (status: string | undefined) => String(status || '').toLowerCase().trim()
  const deriveMergedStatus = (statusSet: Set<string>) => {
    const statuses = new Set(Array.from(statusSet || []).map(normalizeStatus))
    const has = (s: string) => statuses.has(s)
    const hasPending = has('pending')
    const hasPreparing = has('preparing')
    const hasReady = has('ready')
    const hasCompleted = has('completed')
    const hasStarted = hasPreparing || hasReady || hasCompleted

    if (hasPreparing || (hasPending && hasStarted)) return 'preparing'
    if (hasPending) return 'pending'
    if (hasReady) return 'ready'
    if (hasCompleted) return 'completed'
    if (has('cancelled') || has('canceled')) return 'cancelled'
    return 'pending'
  }

  const statusRank = (status: string | undefined) => {
    const normalized = normalizeStatus(status)
    if (normalized === 'completed') return 4
    if (normalized === 'ready') return 3
    if (normalized === 'preparing') return 2
    if (normalized === 'pending') return 1
    if (normalized === 'cancelled' || normalized === 'canceled') return 0
    return -1
  }

  const statusTimestamp = (item: OrderItem & { updated_at?: string; created_at?: string }) => {
    const ts = item?.updated_at || item?.created_at || 0
    const parsed = new Date(ts).getTime()
    return Number.isFinite(parsed) ? parsed : 0
  }

  const creationTimestamp = (item: OrderItem & { created_at?: string }) => {
    const ts = item?.created_at || 0
    const parsed = new Date(ts).getTime()
    return Number.isFinite(parsed) ? parsed : 0
  }

  const shouldReplaceRepresentative = (existing: OrderItem, candidate: OrderItem) => {
    const existingRank = statusRank(existing?.status)
    const candidateRank = statusRank(candidate?.status)
    if (candidateRank !== existingRank) {
      return candidateRank < existingRank
    }
    return statusTimestamp(candidate as any) > statusTimestamp(existing as any)
  }

  const mergeRepresentativeFields = (target: OrderItem & Record<string, unknown>, source: OrderItem & Record<string, unknown>) => {
    target.id = source.id
    target.status = source.status
    if (source.created_at !== undefined) target.created_at = source.created_at
    if (source.updated_at !== undefined) target.updated_at = source.updated_at
    if (source.kds_station_id !== undefined) target.kds_station_id = source.kds_station_id
    if (source.kds_stations !== undefined) target.kds_stations = source.kds_stations
    if ((source as any).kds_station_name !== undefined) (target as any).kds_station_name = (source as any).kds_station_name
    if ((source as any).kds_station_color !== undefined) (target as any).kds_station_color = (source as any).kds_station_color
    if ((source as any).bumped_at !== undefined) (target as any).bumped_at = (source as any).bumped_at
  }

  const grouped = new Map<string, OrderItem & Record<string, unknown>>()

  for (const item of items) {
    const baseKeyParts = [
      item.item_code || '',
      item.item_name || '',
      item.variant_name || '',
      JSON.stringify(item.addons || []),
      item.notes || ''
    ]

    let groupedKey = baseKeyParts.join('|')
    if (!sumQuantities) {
      const eventTs = creationTimestamp(item as OrderItem)
      const eventBucket = eventTs > 0 ? Math.floor(eventTs / 10000) : 0
      groupedKey = `${groupedKey}|${eventBucket}`
    }

    if (grouped.has(groupedKey)) {
      const existing = grouped.get(groupedKey)!
      if (sumQuantities) {
        const addQty = Number(item.quantity) || 1
        const addTotal = Number(item.total_price) || (Number(item.unit_price) || 0) * addQty
        existing.quantity = (Number(existing.quantity) || 0) + addQty
        existing.total_price = (Number(existing.total_price) || 0) + addTotal
      }
      if (!existing._status_set) existing._status_set = new Set([normalizeStatus(existing.status)])
      ;(existing._status_set as Set<string>).add(normalizeStatus(item.status))
      if (shouldReplaceRepresentative(existing, item)) {
        mergeRepresentativeFields(existing, item as any)
      }
      if (item.kds_station_id) {
        if (!existing._kds_station_ids) existing._kds_station_ids = new Set<string>()
        ;(existing._kds_station_ids as Set<string>).add(item.kds_station_id)
      }
    } else {
      const newItem = { ...item } as OrderItem & Record<string, unknown>
      newItem._status_set = new Set([normalizeStatus(item.status)])
      if (item.kds_station_id) {
        newItem._kds_station_ids = new Set([item.kds_station_id])
      }
      grouped.set(groupedKey, newItem)
    }
  }

  return Array.from(grouped.values()).map((item) => {
    const qty = Number(item.quantity) || 1
    const total = Number(item.total_price) || 0
    if (sumQuantities && qty > 0 && total >= 0) {
      item.unit_price = Math.round((total / qty) * 100) / 100
    }
    if (item._status_set) {
      item.status = deriveMergedStatus(item._status_set as Set<string>)
      delete item._status_set
    }
    if (item._kds_station_ids) {
      if (includeKdsInfo) {
        ;(item as any).kds_station_ids = Array.from(item._kds_station_ids as Set<string>)
      }
      delete item._kds_station_ids
      delete item.kds_station_id
    }
    if (!includeKdsInfo) {
      delete (item as any).kds_station_ids
      delete item.kds_stations
      delete (item as any).kds_station_name
      delete (item as any).kds_station_color
    }
    return item as OrderItem
  })
}

const CACHE_TTLS: Record<string, number> = {
  '/menu-items': 30000,
  '/categories': 60000,
  '/pos-settings': 60000,
  '/restaurant-settings': 60000,
  '/kds-stations': 60000,
  '/addon-categories': 60000,
  '/offers': 30000,
}

/** POST /api/orders response (includes waiter draft / approval metadata). */
export interface CreateOrderResponse {
  success: boolean
  error?: string
  isDraft?: boolean
  requires_approval?: boolean
  data?: {
    status?: string
    token_number?: number
    [key: string]: unknown
  }
  pagination?: { page: number; limit: number; total: number; total_pages: number }
}

class ApiService {
  private baseUrl: string
  private cache = new Map<string, { data: any; ts: number }>()

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl
  }

  clearCache(prefix?: string) {
    if (!prefix) { this.cache.clear(); return }
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) this.cache.delete(key)
    }
  }

  private getCacheTTL(endpoint: string): number {
    for (const [pattern, ttl] of Object.entries(CACHE_TTLS)) {
      if (endpoint === pattern || endpoint.startsWith(pattern + '?')) return ttl
    }
    return 0
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<{ success: boolean; data?: T; error?: string; pagination?: { page: number; limit: number; total: number; total_pages: number } }> {
    const method = (options.method || 'GET').toUpperCase()

    if (method !== 'GET') {
      const base = '/' + endpoint.split('/').filter(Boolean)[0]
      this.clearCache(base)
    }

    if (method === 'GET') {
      const ttl = this.getCacheTTL(endpoint)
      if (ttl > 0) {
        const cached = this.cache.get(endpoint)
        if (cached && (Date.now() - cached.ts) < ttl) {
          return cached.data
        }
      }
    }

    try {
      const url = `${this.baseUrl}${endpoint}`
      
      // Get user ID from localStorage, sessionStorage, or session data
      let userId = localStorage.getItem('user-id') || sessionStorage.getItem('user-id')
      
      // If not found, try to get from session data (pos-session key)
      if (!userId) {
        try {
          const sessionStr = sessionStorage.getItem('pos-session') || localStorage.getItem('pos-session')
          if (sessionStr) {
            const session = JSON.parse(sessionStr)
            userId = session?.userId
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      // If still not found, try to get from demo user data
      if (!userId) {
        try {
          const demoUserStr = localStorage.getItem('demo-user')
          if (demoUserStr) {
            const demoUser = JSON.parse(demoUserStr)
            userId = demoUser?.id
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string> || {}),
      }
      
      // Auth token required for protected routes (e.g. POST /api/users)
      const token = localStorage.getItem('pos-auth-token') || sessionStorage.getItem('pos-auth-token')
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
      
      if (userId) {
        headers['user-id'] = userId
        headers['x-user-id'] = userId
      }
      
      const response = await fetch(url, {
        headers,
        ...options,
      })

      let result
      try {
        const text = await response.text()
        try {
          result = JSON.parse(text)
        } catch (parseError) {
          // If response is not valid JSON, return the text or a generic error
          if (!response.ok) {
            throw new Error(`Invalid JSON response from server: ${response.status} ${response.statusText}. Response: ${text.substring(0, 200)}`)
          }
          result = text
        }
      } catch (textError) {
        // If we can't even get the text, throw a generic error
        throw new Error(`Failed to read response: ${response.status} ${response.statusText}`)
      }

      if (!response.ok) {
        // Handle different error formats - always ensure we have a string
        let errorMessage = `Request failed with status ${response.status}`
        
        try {
          if (result) {
            // Try to extract error message from result
            if (typeof result.error === 'string') {
              errorMessage = result.error
            } else if (result.error !== null && result.error !== undefined) {
              // Handle error object
              if (typeof result.error === 'object') {
                // Extract from error object properties
                if (typeof result.error.message === 'string') {
                  errorMessage = result.error.message
                } else if (typeof result.error.details === 'string') {
                  errorMessage = result.error.details
                } else if (typeof result.error.hint === 'string') {
                  errorMessage = result.error.hint
                } else {
                  // Try to stringify, but catch any errors
                  try {
                    const errorStr = JSON.stringify(result.error)
                    if (errorStr && errorStr !== '{}' && errorStr !== 'null' && errorStr !== 'undefined') {
                      errorMessage = errorStr
                    }
                  } catch (e) {
                    // If stringify fails, try toString
                    try {
                      if (result.error.toString && typeof result.error.toString === 'function') {
                        const str = result.error.toString()
                        if (str && str !== '[object Object]') {
                          errorMessage = str
                        }
                      }
                    } catch (toStringError) {
                      // Ignore toString errors
                    }
                  }
                }
              } else if (typeof result.error === 'function') {
                // This shouldn't happen, but handle it
                errorMessage = 'Error object is a function (unexpected)'
              }
            } else if (typeof result.message === 'string') {
              errorMessage = result.message
            } else if (typeof result === 'string') {
              errorMessage = result
            }
          }
        } catch (parseError) {
          // If error parsing fails, use default message with status
          console.warn('Error parsing error response:', parseError, 'Result:', result)
        }
        
        // Ensure errorMessage is always a string - use multiple fallbacks
        if (typeof errorMessage !== 'string') {
          try {
            errorMessage = String(errorMessage)
          } catch (e) {
            try {
              errorMessage = JSON.stringify(errorMessage)
            } catch (e2) {
              errorMessage = `Request failed with status ${response.status}`
            }
          }
        }
        
        // Final safety check
        if (!errorMessage || errorMessage.trim() === '') {
          errorMessage = `Request failed with status ${response.status}`
        }
        
        throw new Error(errorMessage)
      }

      if (method === 'GET' && result?.success) {
        const ttl = this.getCacheTTL(endpoint)
        if (ttl > 0) this.cache.set(endpoint, { data: result, ts: Date.now() })
      }

      return result
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error)
      
      // Safely extract error message
      let errorMsg = 'Unknown error'
      try {
        if (error instanceof Error) {
          errorMsg = error.message || 'Unknown error'
        } else if (typeof error === 'string') {
          errorMsg = error
        } else if (error && typeof error === 'object') {
          // Try to extract message from error object
          if ('message' in error && typeof (error as { message?: unknown }).message === 'string') {
            errorMsg = (error as { message: string }).message
          } else {
            try {
              errorMsg = JSON.stringify(error)
            } catch (e) {
              errorMsg = String(error)
            }
          }
        } else {
          errorMsg = String(error) || 'Unknown error'
        }
      } catch (extractError) {
        // If all else fails, use a generic message
        errorMsg = 'An error occurred while processing the request'
        console.error('Error extracting error message:', extractError)
      }
      
      return {
        success: false,
        error: errorMsg
      }
    }
  }

  // File upload (returns URL path like /uploads/menu-items/abc.jpg)
  async uploadImage(file: File, folder: string = 'menu-items'): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      const formData = new FormData()
      formData.append('file', file)
      const url = `${this.baseUrl}/upload?folder=${encodeURIComponent(folder)}`
      const headers: Record<string, string> = {}
      const token = localStorage.getItem('pos-auth-token') || sessionStorage.getItem('pos-auth-token')
      if (token) headers['Authorization'] = `Bearer ${token}`
      const res = await fetch(url, { method: 'POST', headers, body: formData })
      const json = await res.json()
      if (!res.ok) return { success: false, error: json.error || 'Upload failed' }
      return { success: true, url: json.url }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Upload failed' }
    }
  }

  // Authentication
  async login(email: string, password: string) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
  }

  // Categories
  async getCategories() {
    return this.request('/categories')
  }

  async createCategory(categoryData: any) {
    return this.request('/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData),
    })
  }

  async updateCategory(id: string, categoryData: any) {
    return this.request(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData),
    })
  }

  async deleteCategory(id: string) {
    return this.request(`/categories/${id}`, {
      method: 'DELETE',
    })
  }

  // Allergens (CRUD)
  async getAllergens() {
    return this.request<{ id: string; name: string; icon: string | null; sort_order: number; created_at: string; updated_at: string }[]>('/allergens')
  }

  async getAllergen(id: string) {
    return this.request(`/allergens/${id}`)
  }

  async createAllergen(data: { name: string; icon?: string | null; sort_order?: number }) {
    return this.request('/allergens', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateAllergen(id: string, data: { name?: string; icon?: string | null; sort_order?: number }) {
    return this.request(`/allergens/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteAllergen(id: string) {
    return this.request(`/allergens/${id}`, {
      method: 'DELETE',
    })
  }

  // Menu Items
  async getMenuItems() {
    return this.request('/menu-items')
  }

  async getMenuItem(id: string) {
    return this.request(`/menu-items/${id}`)
  }

  async createMenuItem(menuItemData: any) {
    return this.request('/menu-items', {
      method: 'POST',
      body: JSON.stringify(menuItemData),
    })
  }

  async updateMenuItem(id: string, menuItemData: any) {
    return this.request(`/menu-items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(menuItemData),
    })
  }

  async deleteMenuItem(id: string) {
    return this.request(`/menu-items/${id}`, {
      method: 'DELETE',
    })
  }

  // Tables
  async getTables() {
    return this.request('/tables')
  }

  async createTable(tableData: any) {
    return this.request('/tables', {
      method: 'POST',
      body: JSON.stringify(tableData),
    })
  }

  async updateTable(id: string, tableData: any) {
    return this.request(`/tables/${id}`, {
      method: 'PUT',
      body: JSON.stringify(tableData),
    })
  }

  async deleteTable(id: string) {
    return this.request(`/tables/${id}`, {
      method: 'DELETE',
    })
  }

  // Orders
  async getOrder(id: string) {
    return this.request(`/orders/${id}`)
  }

  async getOrders(options?: { excludeCancelled?: boolean; active_only?: boolean; date_range?: 'today' | 'yesterday'; start_date?: string; end_date?: string }) {
    const params = new URLSearchParams()
    if (options?.excludeCancelled) params.set('exclude_cancelled', '1')
    if (options?.active_only) params.set('active_only', '1')
    if (options?.date_range) params.set('date_range', options.date_range)
    if (options?.start_date) params.set('start_date', options.start_date)
    if (options?.end_date) params.set('end_date', options.end_date)
    const qs = params.toString()
    return this.request(`/orders${qs ? `?${qs}` : ''}`)
  }

  async createOrder(orderData: any): Promise<CreateOrderResponse> {
    return (await this.request('/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    })) as CreateOrderResponse
  }

  async updateOrder(id: string, orderData: any) {
    return this.request(`/orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(orderData),
    })
  }

  async recalculateOrderStatus(id: string) {
    return this.request(`/orders/${id}/recalculate-status`, {
      method: 'POST',
    })
  }

  async updateOrderKdsStatusBulk(
    id: string,
    fromStatus: 'preparing' | 'ready' | 'pending',
    toStatus: 'ready' | 'completed',
    options?: { includePending?: boolean }
  ) {
    return this.request(`/orders/${id}/kds-bulk-status`, {
      method: 'POST',
      body: JSON.stringify({ fromStatus, toStatus, includePending: options?.includePending ?? false }),
    })
  }

  async updateOrderItemKdsStatusBulk(itemId: string, toStatus: 'ready' | 'completed') {
    return this.request(`/order-items/${itemId}/kds-bulk-status`, {
      method: 'POST',
      body: JSON.stringify({ toStatus }),
    })
  }

  async getPendingPaymentOrders() {
    return this.request('/orders/pending-payments')
  }

  async markPaymentReceived(orderId: string, paymentData: { payment_method: string; paid_amount: number; change_amount?: number }) {
    return this.request(`/orders/${orderId}/mark-payment-received`, {
      method: 'POST',
      body: JSON.stringify(paymentData),
    })
  }

  async deleteOrder(id: string, userId?: string) {
    const headers: Record<string, string> = {}
    if (userId) {
      headers['user-id'] = userId
    }
    return this.request(`/orders/${id}`, {
      method: 'DELETE',
      headers,
    })
  }

  // Reservations
  async getReservations() {
    return this.request('/reservations')
  }

  async createReservation(reservationData: any) {
    return this.request('/reservations', {
      method: 'POST',
      body: JSON.stringify(reservationData),
    })
  }

  async updateReservation(id: string, reservationData: any) {
    return this.request(`/reservations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(reservationData),
    })
  }

  async deleteReservation(id: string) {
    return this.request(`/reservations/${id}`, {
      method: 'DELETE',
    })
  }

  // Dashboard
  async getDashboardStats() {
    return this.request('/dashboard/stats')
  }

  // Reports
  async getReports(params: { dateRange?: string; startDate?: string; endDate?: string }) {
    const queryParams = new URLSearchParams()
    if (params.dateRange) queryParams.append('dateRange', params.dateRange)
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    
    const query = queryParams.toString()
    return this.request(`/reports${query ? `?${query}` : ''}`)
  }

  async getClosingReport(params: { dateRange?: string; startDate?: string; endDate?: string }) {
    const queryParams = new URLSearchParams()
    if (params.dateRange) queryParams.append('dateRange', params.dateRange)
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    const query = queryParams.toString()
    return this.request(`/reports/closing${query ? `?${query}` : ''}`)
  }

  /** Orders aggregated by staff who placed them (created_by + profiles) */
  async getStaffOrdersReport(params: { dateRange?: string; startDate?: string; endDate?: string }) {
    const queryParams = new URLSearchParams()
    if (params.dateRange) queryParams.append('dateRange', params.dateRange)
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    const query = queryParams.toString()
    return this.request(`/reports/staff-orders${query ? `?${query}` : ''}`)
  }

  /** Credit Dinner orders (requires credit_dinner_enabled in POS settings; otherwise API returns 403) */
  async getCreditDinnerReport(params: {
    dateRange?: string
    startDate?: string
    endDate?: string
    coName?: string
    customerName?: string
  }) {
    const queryParams = new URLSearchParams()
    if (params.dateRange) queryParams.append('dateRange', params.dateRange)
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    if (params.coName?.trim()) queryParams.append('coName', params.coName.trim())
    if (params.customerName?.trim()) queryParams.append('customerName', params.customerName.trim())
    const query = queryParams.toString()
    return this.request(`/reports/credit-dinner${query ? `?${query}` : ''}`)
  }

  async getCashRegisterClosingReportPreview() {
    return this.request('/cash-register/closing-report-preview')
  }

  // Addon Categories
  async getAddonCategories() {
    return this.request('/addon-categories')
  }

  async createAddonCategory(payload: any) {
    return this.request('/addon-categories', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async updateAddonCategory(id: string, payload: any) {
    return this.request(`/addon-categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  }

  async deleteAddonCategory(id: string) {
    return this.request(`/addon-categories/${id}`, {
      method: 'DELETE',
    })
  }

  // Health check
  async healthCheck() {
    return this.request('/health')
  }

  // Offers
  async getOffers() {
    return this.request('/offers')
  }

  async getOffer(id: string) {
    return this.request(`/offers/${id}`)
  }

  async createOffer(offerData: any) {
    return this.request('/offers', {
      method: 'POST',
      body: JSON.stringify(offerData),
    })
  }

  // Visit-based offers
  async getVisitBasedOffers() {
    return this.request('/visit-based-offers')
  }

  async getAvailableVisitBasedOffers(customerId: string) {
    return this.request(`/visit-based-offers/available/${customerId}`)
  }

  async createVisitBasedOffer(offerData: any) {
    return this.request('/visit-based-offers', {
      method: 'POST',
      body: JSON.stringify(offerData),
    })
  }

  async updateVisitBasedOffer(id: string, offerData: any) {
    return this.request(`/visit-based-offers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(offerData),
    })
  }

  async deleteVisitBasedOffer(id: string) {
    return this.request(`/visit-based-offers/${id}`, {
      method: 'DELETE',
    })
  }

  async useVisitBasedOffer(offerId: string, customerId: string, orderId?: string) {
    return this.request(`/visit-based-offers/${offerId}/use`, {
      method: 'POST',
      body: JSON.stringify({ customer_id: customerId, order_id: orderId }),
    })
  }

  async updateOffer(id: string, offerData: any) {
    return this.request(`/offers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(offerData),
    })
  }

  async deleteOffer(id: string) {
    return this.request(`/offers/${id}`, {
      method: 'DELETE',
    })
  }

  // Customer Management
  async getCustomers() {
    return this.request('/customers')
  }

  async getCustomerByMobile(mobile: string) {
    return this.request(`/customers/mobile/${mobile}`)
  }

  async getCustomerVisitCount(customerId: string) {
    return this.request(`/customers/${customerId}/visit-count`)
  }

  async getRepeatingCustomersCount() {
    return this.request<{ count: number }>('/customers/repeating-count')
  }

  // Cash Register
  async getCashRegisterStatus() {
    return this.request('/cash-register/status')
  }

  async openCashRegister(data: { opening_cash: number; notes?: string; opened_by?: string | null; opened_by_name?: string }) {
    return this.request('/cash-register/open', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async closeCashRegister(data: { closing_cash: number; notes?: string; closed_by?: string | null; closed_by_name?: string }) {
    return this.request('/cash-register/close', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // TOTP Authenticator
  async generateTOTPSecret(userId: string, name?: string, purpose: 'discount' | 'refund' = 'discount') {
    return this.request('/auth/totp/generate', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, name, purpose }),
    })
  }

  async verifyTOTPCode(userId: string, code: string, secretId?: string) {
    return this.request('/auth/totp/verify', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, code, secret_id: secretId }),
    })
  }

  async getTOTPStatus(userId: string, purpose?: 'discount' | 'refund') {
    const url = purpose ? `/auth/totp/status/${userId}?purpose=${purpose}` : `/auth/totp/status/${userId}`
    return this.request(url)
  }

  /** Whether any user has discount/refund TOTP configured (for POS: cashier can use manager's code) */
  async getTOTPConfigured() {
    return this.request<{ data: { discount: { isConfigured: boolean }; refund: { isConfigured: boolean } } }>('/auth/totp/configured')
  }

  async disableTOTP(secretId: string) {
    return this.request(`/auth/totp/${secretId}`, {
      method: 'DELETE',
    })
  }

  async verifyDiscountAuthorization(userId: string | null | undefined, code: string, discountAmount?: number, discountType?: string) {
    const body: Record<string, unknown> = { code, discount_amount: discountAmount, discount_type: discountType }
    if (userId) body.user_id = userId
    return this.request('/auth/totp/verify-discount', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async verifyRefundAuthorization(userId: string, code: string, refundAmount?: number) {
    return this.request('/auth/totp/verify-refund', {
      method: 'POST',
      body: JSON.stringify({ 
        user_id: userId, 
        code,
        refund_amount: refundAmount
      }),
    })
  }

  // Sales Returns and Refunds
  async getReturns() {
    return this.request('/returns')
  }

  async getReturn(id: string) {
    return this.request(`/returns/${id}`)
  }

  async createReturn(data: any) {
    return this.request('/returns', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async authorizeRefund(returnId: string, data: { user_id: string; code: string; refund_amount?: number; refund_method?: string; processed_by?: string }) {
    return this.request(`/returns/${returnId}/authorize`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async processRefund(returnId: string, data: { processed_by?: string }) {
    return this.request(`/returns/${returnId}/process`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async createCustomer(customerData: any) {
    return this.request('/customers', {
      method: 'POST',
      body: JSON.stringify(customerData),
    })
  }

  async updateCustomer(id: string, customerData: any) {
    return this.request(`/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(customerData),
    })
  }

  async deleteCustomer(id: string) {
    return this.request(`/customers/${id}`, {
      method: 'DELETE',
    })
  }

  async getNextGuestNumber() {
    return this.request('/customers/next-guest-number')
  }

  // Customer Fields Management
  async getCustomerFields() {
    return this.request('/customer-fields')
  }

  async createCustomerField(fieldData: any) {
    return this.request('/customer-fields', {
      method: 'POST',
      body: JSON.stringify(fieldData),
    })
  }

  async updateCustomerField(id: string, fieldData: any) {
    return this.request(`/customer-fields/${id}`, {
      method: 'PUT',
      body: JSON.stringify(fieldData),
    })
  }

  async deleteCustomerField(id: string) {
    return this.request(`/customer-fields/${id}`, {
      method: 'DELETE',
    })
  }

  // POS Settings
  async getPOSSettings() {
    return this.request('/pos-settings')
  }

  async updatePOSSettings(settings: any) {
    return this.request('/pos-settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    })
  }

  // Kitchen Display System (KDS) Management
  async getKDSStations() {
    return this.request('/kds-stations')
  }

  /** Current user KDS permissions and assigned station ids. Empty kds_station_ids = all stations. */
  async getMyKdsSettings(): Promise<{
    success: boolean
    data?: {
      can_start_order: boolean
      can_mark_ready: boolean
      can_mark_complete: boolean
      kds_station_ids: string[]
    }
    error?: string
  }> {
    return this.request('/me/kds-settings')
  }

  async createKDSStation(data: any) {
    return this.request('/kds-stations', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateKDSStation(id: string, data: any) {
    return this.request(`/kds-stations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteKDSStation(id: string) {
    return this.request(`/kds-stations/${id}`, {
      method: 'DELETE',
    })
  }

  // Floor Plan KDS Category Assignment Management
  async getFloorPlanKDSCategoryAssignments(floorPlanId: string) {
    return this.request(`/floor-plans/${floorPlanId}/kds-category-assignments`)
  }

  async assignCategoryToKDSStation(floorPlanId: string, kdsStationId: string, categoryId: string, sortOrder?: number) {
    return this.request(`/floor-plans/${floorPlanId}/kds-category-assignments`, {
      method: 'POST',
      body: JSON.stringify({ kds_station_id: kdsStationId, category_id: categoryId, sort_order: sortOrder || 0 }),
    })
  }

  async removeCategoryAssignmentFromFloorPlan(floorPlanId: string, assignmentId: string) {
    return this.request(`/floor-plans/${floorPlanId}/kds-category-assignments/${assignmentId}`, {
      method: 'DELETE',
    })
  }

  async updateCategoryAssignmentSortOrder(floorPlanId: string, assignmentId: string, sortOrder: number) {
    return this.request(`/floor-plans/${floorPlanId}/kds-category-assignments/${assignmentId}`, {
      method: 'PUT',
      body: JSON.stringify({ sort_order: sortOrder }),
    })
  }

  // Restaurant Settings Management
  async getRestaurantSettings() {
    const result = await this.request<Record<string, unknown>>('/restaurant-settings')
    if (result.success && result.data?.app_favicon_url) {
      const fullUrl = resolveImageUrl(String(result.data.app_favicon_url))
      let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
      if (!link) {
        link = document.createElement('link')
        link.rel = 'icon'
        document.head.appendChild(link)
      }
      link.href = fullUrl
    }
    return result
  }

  async updateRestaurantSettings(settings: any) {
    return this.request('/restaurant-settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    })
  }

  // Order Items Management
  async updateOrderItem(id: string, itemData: any) {
    return this.request(`/order-items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(itemData),
    })
  }

  async bumpOrderItem(itemId: string) {
    return this.request(`/order-items/${itemId}/bump`, {
      method: 'POST',
    })
  }

  async bumpOrder(orderId: string) {
    return this.request(`/orders/${orderId}/bump`, {
      method: 'POST',
    })
  }

  // Accounting - Chart of Accounts
  async getChartOfAccounts(params?: { page?: number; limit?: number }) {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    const queryString = queryParams.toString()
    return this.request(`/accounting/chart-of-accounts${queryString ? `?${queryString}` : ''}`)
  }

  async getAccountLedger(accountId: string, filters?: { start_date?: string; end_date?: string; page?: number; limit?: number }) {
    const params = new URLSearchParams()
    if (filters?.start_date) params.append('start_date', filters.start_date)
    if (filters?.end_date) params.append('end_date', filters.end_date)
    if (filters?.page) params.append('page', filters.page.toString())
    if (filters?.limit) params.append('limit', filters.limit.toString())
    const queryString = params.toString()
    return this.request(`/accounting/chart-of-accounts/${accountId}/ledger${queryString ? `?${queryString}` : ''}`)
  }

  async createChartOfAccount(data: any) {
    return this.request('/accounting/chart-of-accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateChartOfAccount(id: string, data: any) {
    return this.request(`/accounting/chart-of-accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // Accounting - Journal Entries
  async getJournalEntries(params?: { page?: number; limit?: number }) {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    const queryString = queryParams.toString()
    return this.request(`/accounting/journal-entries${queryString ? `?${queryString}` : ''}`)
  }

  async getJournalEntryById(id: string) {
    return this.request(`/accounting/journal-entries/${id}`)
  }

  async createJournalEntry(data: any) {
    return this.request('/accounting/journal-entries', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async approveJournalEntry(id: string) {
    return this.request(`/accounting/journal-entries/${id}/approve`, {
      method: 'POST',
    })
  }

  async deleteJournalEntry(id: string, userId?: string) {
    const headers: Record<string, string> = {}
    if (userId) headers['user-id'] = userId
    return this.request(`/accounting/journal-entries/${id}`, {
      method: 'DELETE',
      headers,
    })
  }

  // Accounting - Vendors
  async getVendors(params?: { page?: number; limit?: number }) {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    const queryString = queryParams.toString()
    return this.request(`/accounting/vendors${queryString ? `?${queryString}` : ''}`)
  }

  async createVendor(data: any) {
    return this.request('/accounting/vendors', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateVendor(id: string, data: any) {
    return this.request(`/accounting/vendors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteVendor(id: string) {
    return this.request(`/accounting/vendors/${id}`, {
      method: 'DELETE',
    })
  }

  // Accounting - Expense Categories
  async getExpenseCategories(params?: { page?: number; limit?: number }) {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    const queryString = queryParams.toString()
    return this.request(`/accounting/expense-categories${queryString ? `?${queryString}` : ''}`)
  }

  async getExpenseAnalytics() {
    return this.request('/accounting/expenses/analytics')
  }

  // Inventory Locations
  async getInventoryLocations() {
    return this.request('/inventory/locations')
  }

  async createInventoryLocation(locationData: any) {
    return this.request('/inventory/locations', {
      method: 'POST',
      body: JSON.stringify(locationData),
    })
  }

  async updateInventoryLocation(id: string, locationData: any) {
    return this.request(`/inventory/locations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(locationData),
    })
  }

  async deleteInventoryLocation(id: string) {
    return this.request(`/inventory/locations/${id}`, {
      method: 'DELETE',
    })
  }

  // Inventory Items
  async getInventory() {
    return this.request('/inventory')
  }

  async createInventoryItem(itemData: any) {
    return this.request('/inventory', {
      method: 'POST',
      body: JSON.stringify(itemData),
    })
  }

  async updateInventoryItem(id: string, itemData: any) {
    return this.request(`/inventory/${id}`, {
      method: 'PUT',
      body: JSON.stringify(itemData),
    })
  }

  async updateInventoryStock(id: string, stock: number) {
    return this.request(`/inventory/${id}/stock`, {
      method: 'PATCH',
      body: JSON.stringify({ stock }),
    })
  }

  async deleteInventoryItem(id: string) {
    return this.request(`/inventory/${id}`, {
      method: 'DELETE',
    })
  }

  // Stock Transactions
  async createStockTransaction(transactionData: any) {
    return this.request('/inventory/transactions', {
      method: 'POST',
      body: JSON.stringify(transactionData),
    })
  }

  async getStockTransactions(params?: {
    inventory_id?: string
    transaction_type?: string
    start_date?: string
    end_date?: string
    reference_type?: string
    page?: number
    limit?: number
  }) {
    const queryParams = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString())
        }
      })
    }
    const queryString = queryParams.toString()
    return this.request(`/inventory/transactions${queryString ? `?${queryString}` : ''}`)
  }

  async getStockReports(params: {
    inventory_id?: string
    start_date: string
    end_date: string
    group_by?: 'day' | 'week' | 'month'
  }) {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString())
      }
    })
    return this.request(`/inventory/transactions/reports?${queryParams.toString()}`)
  }

  // Inventory Categories
  async getInventoryCategories() {
    return this.request('/inventory/categories')
  }

  async createInventoryCategory(categoryData: any) {
    return this.request('/inventory/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData),
    })
  }

  async updateInventoryCategory(id: string, categoryData: any) {
    return this.request(`/inventory/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData),
    })
  }

  async deleteInventoryCategory(id: string) {
    return this.request(`/inventory/categories/${id}`, {
      method: 'DELETE',
    })
  }

  async createExpenseCategory(categoryData: any) {
    return this.request('/accounting/expense-categories', {
      method: 'POST',
      body: JSON.stringify(categoryData),
    })
  }

  async updateExpenseCategory(id: string, categoryData: any) {
    return this.request(`/accounting/expense-categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData),
    })
  }

  async deleteExpenseCategory(id: string) {
    return this.request(`/accounting/expense-categories/${id}`, {
      method: 'DELETE',
    })
  }

  // Accounting - Reports
  async getTrialBalance(filters: { start_date?: string; end_date?: string }) {
    const params = new URLSearchParams()
    if (filters.start_date) params.append('start_date', filters.start_date)
    if (filters.end_date) params.append('end_date', filters.end_date)
    return this.request(`/accounting/reports/trial-balance?${params.toString()}`)
  }

  async getProfitLoss(filters: { start_date?: string; end_date?: string }) {
    const params = new URLSearchParams()
    if (filters.start_date) params.append('start_date', filters.start_date)
    if (filters.end_date) params.append('end_date', filters.end_date)
    return this.request(`/accounting/reports/profit-loss?${params.toString()}`)
  }

  async getBalanceSheet(filters: { as_of_date?: string }) {
    const params = new URLSearchParams()
    if (filters.as_of_date) params.append('as_of_date', filters.as_of_date)
    return this.request(`/accounting/reports/balance-sheet?${params.toString()}`)
  }

  async getPayables(filters: { as_of_date?: string; aging_days?: number; aging_filter?: string }) {
    const params = new URLSearchParams()
    if (filters.as_of_date) params.append('as_of_date', filters.as_of_date)
    if (filters.aging_days !== undefined && filters.aging_days !== null) params.append('aging_days', filters.aging_days.toString())
    if (filters.aging_filter) params.append('aging_filter', filters.aging_filter)
    return this.request(`/accounting/reports/payables?${params.toString()}`)
  }

  async getReceivables(filters: { as_of_date?: string; aging_days?: number; aging_filter?: string }) {
    const params = new URLSearchParams()
    if (filters.as_of_date) params.append('as_of_date', filters.as_of_date)
    if (filters.aging_days !== undefined && filters.aging_days !== null) params.append('aging_days', filters.aging_days.toString())
    if (filters.aging_filter) params.append('aging_filter', filters.aging_filter)
    return this.request(`/accounting/reports/receivables?${params.toString()}`)
  }

  async getVATReport(filters: { start_date?: string; end_date?: string }) {
    const params = new URLSearchParams()
    if (filters.start_date) params.append('start_date', filters.start_date)
    if (filters.end_date) params.append('end_date', filters.end_date)
    return this.request(`/accounting/reports/vat?${params.toString()}`)
  }

  async getCashFlow(filters: { start_date?: string; end_date?: string }) {
    const params = new URLSearchParams()
    if (filters.start_date) params.append('start_date', filters.start_date)
    if (filters.end_date) params.append('end_date', filters.end_date)
    return this.request(`/accounting/reports/cash-flow?${params.toString()}`)
  }

  async getGeneralLedger(filters: { account_id?: string; start_date?: string; end_date?: string; page?: number; limit?: number }) {
    const params = new URLSearchParams()
    if (filters.account_id) params.append('account_id', filters.account_id)
    if (filters.start_date) params.append('start_date', filters.start_date)
    if (filters.end_date) params.append('end_date', filters.end_date)
    if (filters.page) params.append('page', filters.page.toString())
    if (filters.limit) params.append('limit', filters.limit.toString())
    return this.request(`/accounting/reports/general-ledger?${params.toString()}`)
  }

  async getJournalEntryListing(filters: { start_date?: string; end_date?: string; status?: string; source_module?: string; entry_number?: string; page?: number; limit?: number }) {
    const params = new URLSearchParams()
    if (filters.start_date) params.append('start_date', filters.start_date)
    if (filters.end_date) params.append('end_date', filters.end_date)
    if (filters.status) params.append('status', filters.status)
    if (filters.source_module) params.append('source_module', filters.source_module)
    if (filters.entry_number) params.append('entry_number', filters.entry_number)
    if (filters.page) params.append('page', filters.page.toString())
    if (filters.limit) params.append('limit', filters.limit.toString())
    return this.request(`/accounting/reports/journal-entries?${params.toString()}`)
  }

  async getSalesByCategory(filters: { start_date?: string; end_date?: string; order_type?: string }) {
    const params = new URLSearchParams()
    if (filters.start_date) params.append('start_date', filters.start_date)
    if (filters.end_date) params.append('end_date', filters.end_date)
    if (filters.order_type) params.append('order_type', filters.order_type)
    return this.request(`/accounting/reports/sales-by-category?${params.toString()}`)
  }

  async getExpenseByCategory(filters: { start_date?: string; end_date?: string; category_id?: string; expense_type?: string }) {
    const params = new URLSearchParams()
    if (filters.start_date) params.append('start_date', filters.start_date)
    if (filters.end_date) params.append('end_date', filters.end_date)
    if (filters.category_id) params.append('category_id', filters.category_id)
    if (filters.expense_type) params.append('expense_type', filters.expense_type)
    return this.request(`/accounting/reports/expense-by-category?${params.toString()}`)
  }

  // Accounting - Expenses
  async getExpenses(params?: { page?: number; limit?: number }) {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    const queryString = queryParams.toString()
    return this.request(`/accounting/expenses${queryString ? `?${queryString}` : ''}`)
  }

  async getExpense(id: string) {
    return this.request(`/accounting/expenses/${id}`)
  }

  async createExpense(data: any) {
    return this.request('/accounting/expenses', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateExpense(id: string, data: any) {
    return this.request(`/accounting/expenses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteExpense(id: string) {
    return this.request(`/accounting/expenses/${id}`, {
      method: 'DELETE',
    })
  }

  async recordExpensePayment(expenseId: string, paymentData: {
    payment_date: string
    payment_amount: number
    payment_method: string
    payment_reference?: string | null
    notes?: string | null
  }) {
    return this.request(`/accounting/expenses/${expenseId}/record-payment`, {
      method: 'POST',
      body: JSON.stringify(paymentData),
    })
  }

  // Accounting - Reports
  // HR - Employees
  async getEmployees() {
    return this.request('/hr/employees')
  }

  async createEmployee(data: any) {
    return this.request('/hr/employees', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateEmployee(id: string, data: any) {
    return this.request(`/hr/employees/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteEmployee(id: string) {
    return this.request(`/hr/employees/${id}`, {
      method: 'DELETE',
    })
  }

  // HR - Attendance
  async getAttendance() {
    return this.request('/hr/attendance')
  }

  async createAttendance(data: any) {
    return this.request('/hr/attendance', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateAttendance(id: string, data: any) {
    return this.request(`/hr/attendance/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteAttendance(id: string) {
    return this.request(`/hr/attendance/${id}`, {
      method: 'DELETE',
    })
  }

  // HR - Leaves
  async getLeaveTypes(activeOnly?: boolean) {
    const params = activeOnly ? '?active_only=true' : ''
    return this.request(`/hr/leave-types${params}`)
  }

  async createLeaveType(data: any) {
    return this.request('/hr/leave-types', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateLeaveType(id: string, data: any) {
    return this.request(`/hr/leave-types/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteLeaveType(id: string) {
    return this.request(`/hr/leave-types/${id}`, {
      method: 'DELETE',
    })
  }

  async getLeaveRequests() {
    return this.request('/hr/leave-requests')
  }

  async createLeaveRequest(data: any) {
    return this.request('/hr/leave-requests', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateLeaveRequest(id: string, data: any) {
    return this.request(`/hr/leave-requests/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async approveLeaveRequest(id: string, approvedBy?: string) {
    return this.request(`/hr/leave-requests/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approved_by: approvedBy || null }),
    })
  }

  async rejectLeaveRequest(id: string, approvedBy?: string, rejectionReason?: string) {
    return this.request(`/hr/leave-requests/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ 
        approved_by: approvedBy || null,
        rejection_reason: rejectionReason || null
      }),
    })
  }

  async deleteLeaveRequest(id: string) {
    return this.request(`/hr/leave-requests/${id}`, {
      method: 'DELETE',
    })
  }

  // HR - Payroll
  async getPayroll(id: string) {
    return this.request(`/hr/payroll/${id}`)
  }

  async getPayrollList() {
    return this.request('/hr/payroll')
  }

  async createPayroll(data: any) {
    return this.request('/hr/payroll', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updatePayroll(id: string, data: any) {
    return this.request(`/hr/payroll/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deletePayroll(id: string) {
    return this.request(`/hr/payroll/${id}`, {
      method: 'DELETE',
    })
  }

  async recalculatePayroll(id: string) {
    return this.request(`/hr/payroll/recalculate/${id}`, {
      method: 'POST',
    })
  }

  async recalculateAllPayroll() {
    return this.request('/hr/payroll/recalculate-all', {
      method: 'POST',
    })
  }

  async processPayroll(id: string, data?: { status?: string; payment_method?: string; payment_reference?: string }) {
    return this.request(`/hr/payroll/process/${id}`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    })
  }

  async processPayrollBulk(payrollIds: string[], data?: { status?: string; payment_method?: string; payment_reference?: string }) {
    return this.request('/hr/payroll/process-bulk', {
      method: 'POST',
      body: JSON.stringify({
        payroll_ids: payrollIds,
        ...data
      }),
    })
  }

  // HR - Loans & Advances
  async getLoans(employeeId?: string, loanType?: string) {
    const params = new URLSearchParams()
    if (employeeId) params.append('employee_id', employeeId)
    if (loanType) params.append('loan_type', loanType)
    return this.request(`/hr/loans?${params.toString()}`)
  }

  async createLoan(data: any) {
    return this.request('/hr/loans', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateLoan(id: string, data: any) {
    return this.request(`/hr/loans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteLoan(id: string) {
    return this.request(`/hr/loans/${id}`, {
      method: 'DELETE',
    })
  }

  async getLoanTypes() {
    return this.request('/hr/loan-types')
  }

  // HR - Loan Payments/Repayments
  async getLoanPayments(loanId?: string) {
    const params = new URLSearchParams()
    if (loanId) params.append('loan_id', loanId)
    return this.request(`/hr/loan-payments?${params.toString()}`)
  }

  async createLoanPayment(data: any) {
    return this.request('/hr/loan-payments', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async deleteLoanPayment(id: string) {
    return this.request(`/hr/loan-payments/${id}`, {
      method: 'DELETE',
    })
  }

  // Payments Management
  async getPayments(filters?: {
    payment_type?: string
    payment_category?: string
    payment_method?: string
    start_date?: string
    end_date?: string
    search?: string
    is_reconciled?: boolean
    page?: number
    limit?: number
  }) {
    const params = new URLSearchParams()
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value))
        }
      })
    }
    return this.request(`/payments?${params.toString()}`)
  }

  async getPayment(id: string) {
    return this.request(`/payments/${id}`)
  }

  async createPayment(data: any) {
    return this.request('/payments', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updatePayment(id: string, data: any) {
    return this.request(`/payments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deletePayment(id: string) {
    return this.request(`/payments/${id}`, {
      method: 'DELETE',
    })
  }

  // Recipe Management
  async getRecipes(menuItemId?: string, variantId?: string, addonId?: string) {
    const params = new URLSearchParams()
    if (menuItemId) params.append('menu_item_id', menuItemId)
    if (variantId) params.append('variant_id', variantId)
    if (addonId) params.append('addon_id', addonId)
    return this.request(`/recipes?${params.toString()}`)
  }

  async createRecipe(recipeData: any) {
    return this.request('/recipes', {
      method: 'POST',
      body: JSON.stringify(recipeData),
    })
  }

  async updateRecipe(id: string, recipeData: any) {
    return this.request(`/recipes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(recipeData),
    })
  }

  async deleteRecipe(id: string) {
    return this.request(`/recipes/${id}`, {
      method: 'DELETE',
    })
  }

  // Print Receipt
  async printReceipt(order: any, restaurantSettings: any, printerConfig: any, receiptLayout?: any[], printerWidth?: number, receiptLogoUrl?: string) {
    return this.request('/print/receipt', {
      method: 'POST',
      body: JSON.stringify({ 
        order, 
        restaurantSettings, 
        printerConfig,
        receiptLayout,
        printerWidth,
        receiptLogoUrl
      }),
    })
  }

  async printClosingReport(report: any, restaurantSettings: any, printerConfig: any) {
    return this.request('/print/closing-report', {
      method: 'POST',
      body: JSON.stringify({ report, restaurantSettings, printerConfig }),
    })
  }

  // Generate ZATCA QR Code
  async generateZATCAQRCode(order: any, restaurantSettings: any, invoiceNumber?: string) {
    return this.request('/zatca/generate-qr', {
      method: 'POST',
      body: JSON.stringify({ 
        order, 
        restaurantSettings,
        invoiceNumber
      }),
    })
  }

  // ============================================
  // Users, Roles & Permissions
  // ============================================

  // Users
  async getUsers() {
    return this.request('/users')
  }

  async getUser(userId: string) {
    return this.request(`/users/${userId}`)
  }

  async createUser(userData: {
    email: string
    password: string
    name: string
    phone?: string
    role_ids?: string[]
    default_floor_plan_id?: string
  }) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    })
  }

  async updateUser(userId: string, userData: {
    name?: string
    phone?: string
    email?: string
    password?: string
    role_ids?: string[]
    default_floor_plan_id?: string
    kds_can_start_order?: boolean
    kds_can_mark_ready?: boolean
    kds_can_mark_complete?: boolean
    kds_station_ids?: string[]
  }) {
    return this.request(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    })
  }

  async deleteUser(userId: string) {
    return this.request(`/users/${userId}`, {
      method: 'DELETE',
    })
  }

  async assignRolesToUser(userId: string, roleIds: string[]) {
    return this.request(`/users/${userId}/roles`, {
      method: 'POST',
      body: JSON.stringify({ role_ids: roleIds }),
    })
  }

  async removeRoleFromUser(userId: string, roleId: string) {
    return this.request(`/users/${userId}/roles/${roleId}`, {
      method: 'DELETE',
    })
  }

  async getUserPermissions(userId: string) {
    return this.request(`/users/${userId}/permissions`)
  }

  // Roles
  async getRoles() {
    return this.request('/roles')
  }

  async getRole(roleId: string) {
    return this.request(`/roles/${roleId}`)
  }

  async createRole(roleData: {
    name: string
    description?: string
  }) {
    return this.request('/roles', {
      method: 'POST',
      body: JSON.stringify(roleData),
    })
  }

  async updateRole(roleId: string, roleData: {
    name?: string
    description?: string
    is_active?: boolean
    start_page?: string | null
  }) {
    return this.request(`/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(roleData),
    })
  }

  async deleteRole(roleId: string) {
    return this.request(`/roles/${roleId}`, {
      method: 'DELETE',
    })
  }

  async assignPermissionsToRole(roleId: string, permissionIds: string[]) {
    return this.request(`/roles/${roleId}/permissions`, {
      method: 'POST',
      body: JSON.stringify({ permission_ids: permissionIds }),
    })
  }

  async removePermissionFromRole(roleId: string, permissionId: string) {
    return this.request(`/roles/${roleId}/permissions/${permissionId}`, {
      method: 'DELETE',
    })
  }

  // Permissions
  async getPermissions(filters?: { category?: string; resource?: string }) {
    const queryParams = new URLSearchParams()
    if (filters?.category) queryParams.append('category', filters.category)
    if (filters?.resource) queryParams.append('resource', filters.resource)
    
    const query = queryParams.toString()
    return this.request(`/permissions${query ? `?${query}` : ''}`)
  }

  async getPermissionsGrouped() {
    return this.request('/permissions/grouped')
  }

  // Invoices
  async getInvoices(filters?: { status?: string; start_date?: string; end_date?: string; search?: string }) {
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.start_date) params.append('start_date', filters.start_date)
    if (filters?.end_date) params.append('end_date', filters.end_date)
    if (filters?.search) params.append('search', filters.search)
    return this.request(`/invoices?${params.toString()}`)
  }

  async getBillRequest(id: string) {
    return this.request(`/bill-requests/${id}`)
  }

  async cancelBillRequest(id: string) {
    return this.request(`/bill-requests/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  }

  // Data Management (Super Admin Only)
  async clearOrders() {
    return this.request('/admin/clear/orders', { method: 'DELETE' })
  }

  async clearLedgers() {
    return this.request('/admin/clear/ledgers', { method: 'DELETE' })
  }

  async clearExpenses() {
    return this.request('/admin/clear/expenses', { method: 'DELETE' })
  }

  async clearInventory() {
    return this.request('/admin/clear/inventory', { method: 'DELETE' })
  }

  async clearAttendances() {
    return this.request('/admin/clear/attendances', { method: 'DELETE' })
  }

  async clearPayrolls() {
    return this.request('/admin/clear/payrolls', { method: 'DELETE' })
  }

  async clearLeaves() {
    return this.request('/admin/clear/leaves', { method: 'DELETE' })
  }

  async clearReservations() {
    return this.request('/admin/clear/reservations', { method: 'DELETE' })
  }

  async clearCustomers() {
    return this.request('/admin/clear/customers', { method: 'DELETE' })
  }

  async clearAiData() {
    return this.request('/admin/clear/ai-data', { method: 'DELETE' })
  }

  async clearAllData() {
    return this.request('/admin/clear/all', { method: 'DELETE' })
  }
}

export const apiService = new ApiService()

// Export types
export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  error?: string
}

export default apiService
